import ReactDOM from 'react-dom/client'
import { HelmetProvider } from 'react-helmet-async'
import { Provider } from 'react-redux'
import * as Sentry from '@sentry/react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import posthog from 'posthog-js'
import { PostHogProvider } from 'posthog-js/react'
import { PersistGate } from 'redux-persist/integration/react'

import ErrorFallback from './components/ErrorFallback'
import { ToastContextProvider } from './components/Toast/ToastContext'
import { AnalyticsProvider } from './hooks/AnalyticsProvider'
import { applySentrySampleRatesFromFeatureFlag, initSentry } from './utils/sentry'
import App from './App'
import reportWebVitals from './reportWebVitals'
import { persistor, store } from './store'

import './index.scss'
import './styles/globals.css'

initSentry()

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000, // 1 minute
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})

// Initialize PostHog with proper environment variable validation
const posthogKey = import.meta.env.VITE_POSTHOG_KEY
const posthogHost = import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com'

if (posthogKey) {
  posthog.init(posthogKey, {
    // eslint-disable-next-line
    api_host: posthogHost,
    defaults: '2025-05-24',
    loaded: (posthog) => {
      if (process.env.NODE_ENV === 'development') {
        posthog.debug() // Enable debug mode in development
      }
    },
  })
  applySentrySampleRatesFromFeatureFlag()
} else {
  console.warn('PostHog not initialized: VITE_POSTHOG_KEY environment variable is not set')
}

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement)
root.render(
  // <React.StrictMode>
  <QueryClientProvider client={queryClient}>
    <Sentry.ErrorBoundary fallback={({ resetError }) => <ErrorFallback resetError={resetError} />}>
      <PostHogProvider client={posthog}>
        <Provider store={store}>
          <PersistGate loading={null} persistor={persistor}>
            <HelmetProvider>
              <AnalyticsProvider>
                <ToastContextProvider>
                  <App />
                </ToastContextProvider>
              </AnalyticsProvider>
            </HelmetProvider>
          </PersistGate>
        </Provider>
      </PostHogProvider>
    </Sentry.ErrorBoundary>
    {process.env.NODE_ENV === 'development' && <ReactQueryDevtools initialIsOpen={false} />}
  </QueryClientProvider>
  // </React.StrictMode>
)

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals()
